import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

export interface DonationProgress {
  total_raised: number
  total_tails_funded: number
  total_donations: number
  goal_amount: number
  goal_tails: number
  percent_complete: number
  tails_remaining: number
  // Overtime/stretch goal fields
  is_overtime: boolean
  overtime_tails: number
  overtime_amount: number
  stretch_goal_tails: number
  stretch_goal_amount: number
  stretch_percent_complete: number
}

export interface DonationReservation {
  reservation_id: string
  amount: number
  tails_reserved: number
  expires_at: string
}

interface DonationContextType {
  // Donation amount
  amount: number
  setAmount: (amount: number) => void

  // Tip
  tipAmount: number
  setTipAmount: (amount: number) => void

  // Total
  totalAmount: number

  // Donor info
  donorEmail: string
  setDonorEmail: (email: string) => void
  donorName: string
  setDonorName: (name: string) => void

  // Campaign progress
  progress: DonationProgress | null
  refreshProgress: () => Promise<void>

  // Reservation
  reservation: DonationReservation | null
  createReservation: (amount: number) => Promise<void>

  // State
  loading: boolean
  error: string | null
  clearError: () => void

  // Helpers
  getTailsFunded: (amount: number) => number
}

const DonationContext = createContext<DonationContextType | null>(null)

export const useDonation = () => {
  const context = useContext(DonationContext)
  if (!context) {
    throw new Error('useDonation must be used within a DonationProvider')
  }
  return context
}

const COST_PER_TAIL = 8.40

export const DonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [amount, setAmount] = useState(8.40) // Default to 1 Tail tier
  const [tipAmount, setTipAmount] = useState(0)
  const [donorEmail, setDonorEmail] = useState('')
  const [donorName, setDonorName] = useState('')
  const [progress, setProgress] = useState<DonationProgress | null>(null)
  const [reservation, setReservation] = useState<DonationReservation | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Calculate total amount
  const totalAmount = amount + tipAmount

  // Calculate tails funded for a given amount
  const getTailsFunded = useCallback((amt: number): number => {
    return Math.floor(amt / COST_PER_TAIL)
  }, [])

  // Default progress values (used when API fails or is not deployed)
  const defaultProgress: DonationProgress = {
    total_raised: 0,
    total_tails_funded: 0,
    total_donations: 0,
    goal_amount: 2016.00,
    goal_tails: 240,
    percent_complete: 0,
    tails_remaining: 240,
    is_overtime: false,
    overtime_tails: 0,
    overtime_amount: 0,
    stretch_goal_tails: 240,
    stretch_goal_amount: 2016.00,
    stretch_percent_complete: 0
  }

  // Refresh campaign progress
  const refreshProgress = useCallback(async () => {
    try {
      const response = await fetch('/.netlify/functions/get-donation-progress')
      if (!response.ok) {
        throw new Error('Failed to fetch donation progress')
      }
      const data = await response.json()
      // Ensure we have valid data, otherwise use defaults
      if (data && typeof data.total_raised === 'number') {
        setProgress(data)
      } else {
        console.warn('Invalid progress data received, using defaults')
        setProgress(defaultProgress)
      }
    } catch (err) {
      console.error('Error fetching donation progress:', err)
      // Set default progress on error to prevent infinite loading state
      setProgress(defaultProgress)
    }
  }, [])

  // Create a reservation for the donation amount
  const createReservation = useCallback(async (amt: number) => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/.netlify/functions/create-donation-reservation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: amt }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to create reservation')
      }

      const data = await response.json()
      setReservation(data)
    } catch (err: any) {
      console.error('Error creating reservation:', err)
      setError(err.message || 'Failed to create reservation')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Clear error state
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Load initial progress on mount
  useEffect(() => {
    refreshProgress()

    // Poll for progress updates every 30 seconds
    const interval = setInterval(refreshProgress, 30000)

    return () => clearInterval(interval)
  }, [refreshProgress])

  return (
    <DonationContext.Provider
      value={{
        amount,
        setAmount,
        tipAmount,
        setTipAmount,
        totalAmount,
        donorEmail,
        setDonorEmail,
        donorName,
        setDonorName,
        progress,
        refreshProgress,
        reservation,
        createReservation,
        loading,
        error,
        clearError,
        getTailsFunded,
      }}
    >
      {children}
    </DonationContext.Provider>
  )
}

export default DonationProvider
